import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import './FAQ.css';

const faqs = [
  {
    question: 'Do I need to provide my own domain name?',
    answer: 'If you already own a domain, we can use that. If not, we\'ll help you register one as part of the project setup.',
  },
  {
    question: 'What happens if I don\'t like the design?',
    answer: 'Every package includes multiple rounds of revisions (2 for Basic, 5 for Standard, unlimited within scope for Premium). We won\'t consider the project complete until you\'re happy with it.',
  },
  {
    question: 'Can I make changes to my site after it\'s live?',
    answer: 'Yes. Our monthly hosting & support plan covers minor updates, and you can always upgrade to a Website Care Plan for more frequent changes. For Basic tier sites, we also provide a simple guide so you can make basic edits yourself.',
  },
  {
    question: 'How long does it take to get my site live?',
    answer: 'Delivery time depends on the package — typically 5-7 days for the Online Presence Pack, up to 21 days for the Full Business Suite. Timelines start once the deposit is paid and we\'ve received your content.',
  },
  {
    question: 'How does payment work?',
    answer: 'We use a simple 50/50 structure — 50% deposit to begin the project, and 50% on delivery before the site goes live. Monthly hosting & support fees begin once your site launches.',
  },
  {
    question: 'Do you work with businesses outside Nashville?',
    answer: 'Yes — while we\'re based in Nashville and love working with local businesses, we work with clients remotely anywhere.',
  },
];

const FAQItem = memo(({ question, answer, isOpen, onToggle, index }) => {
  const contentRef = useRef(null);

  return (
    <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`}>
      <button
        className="faq__question"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
        id={`faq-question-${index}`}
      >
        <span>{question}</span>
        <ChevronDown size={20} className="faq__chevron" />
      </button>
      <div
        className="faq__answer-wrapper"
        id={`faq-answer-${index}`}
        role="region"
        aria-labelledby={`faq-question-${index}`}
        style={{ maxHeight: isOpen ? contentRef.current?.scrollHeight + 'px' : '0px' }}
      >
        <div className="faq__answer" ref={contentRef}>
          <p>{answer}</p>
        </div>
      </div>
    </div>
  );
});

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleToggle = useCallback((index) => {
    setOpenIndex((prev) => prev === index ? null : index);
  }, []);

  return (
    <section className="faq section" id="faq" ref={sectionRef}>
      <div className="container">
        <div className={`faq__header ${visible ? 'faq__fade-in' : ''}`}>
          <span className="section-label">FAQ</span>
          <h2 className="section-title">Common questions, answered.</h2>
          <p className="section-subtitle">Everything you need to know before getting started.</p>
        </div>

        <div className={`faq__list ${visible ? 'faq__fade-in faq__fade-in--delay' : ''}`}>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
